import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import KpiCard from "@/components/KpiCard";
import api from "@/lib/api";
import { IndianRupee, Users, Building2, ShieldCheck, ShieldOff } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const PLAN_COLORS = { TRIAL: "#94a3b8", BASIC: "#2563eb", PRO: "#0f766e", ENTERPRISE: "#7c3aed" };
const STATUS_COLORS = { ACTIVE: "#16a34a", EXPIRED: "#f59e0b" };

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data));
  }, []);

  if (!data) return <AdminLayout><p className="text-slate-500">Loading analytics...</p></AdminLayout>;

  const planChartData = data.tier_breakdown.map((t) => ({ name: t.label, count: t.total, fill: PLAN_COLORS[t.plan] }));
  const statusChartData = Object.entries(data.clinics_by_status).map(([status, count]) => ({
    name: status,
    value: count,
    fill: STATUS_COLORS[status],
  }));
  const totalActive = data.clinics_by_status.ACTIVE || 0;
  const totalExpired = data.clinics_by_status.EXPIRED || 0;

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900" data-testid="admin-analytics-heading">Platform Analytics</h1>
      <p className="mt-1 text-sm text-slate-600">Revenue, subscription mix and clinic health across the platform.</p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="analytics-total-revenue" label="Total Monthly Revenue" value={`₹${data.total_revenue.toLocaleString("en-IN")}`} icon={IndianRupee} accent="text-teal-700" />
        <KpiCard testId="analytics-total-leads" label="Total Leads" value={data.total_leads} icon={Users} />
        <KpiCard testId="analytics-active-clinics" label="Active Clinics" value={totalActive} icon={ShieldCheck} accent="text-green-600" />
        <KpiCard testId="analytics-expired-clinics" label="Expired Clinics" value={totalExpired} icon={ShieldOff} accent="text-amber-600" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="chart-clinics-by-plan">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Building2 className="h-4 w-4" /> Clinics per Subscription Tier</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={planChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {planChartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="chart-clinics-by-status">
          <h2 className="font-semibold text-slate-900">Active vs Expired</h2>
          <div className="mt-4 flex h-64 items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusChartData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {statusChartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" height={30} />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6 lg:col-span-2" data-testid="chart-leads-trend">
          <h2 className="font-semibold text-slate-900">Leads — Last 6 Months</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.leads_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
                <Line type="monotone" dataKey="count" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 4, fill: "#0f766e" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h2 className="mb-3 font-semibold text-slate-900">Tier Breakdown</h2>
        <Table data-testid="tier-breakdown-table">
          <TableHeader>
            <TableRow className="border-b border-slate-200 bg-slate-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Clinics</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Expired</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Monthly Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.tier_breakdown.map((t, i) => (
              <TableRow key={t.plan} className={i % 2 === 1 ? "bg-slate-50/60" : ""} data-testid={`tier-row-${t.plan}`}>
                <TableCell className="font-medium text-slate-900">{t.label}</TableCell>
                <TableCell>{t.total}</TableCell>
                <TableCell className="text-green-700">{t.active}</TableCell>
                <TableCell className="text-amber-600">{t.expired}</TableCell>
                <TableCell className="font-medium text-teal-700">₹{t.monthly_revenue.toLocaleString("en-IN")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AdminLayout>
  );
}
